import { useMemo } from 'react'
import cx from 'classnames'
import { Modal, useModals } from 'almanac-ui/Modal'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { Check2 } from 'blaze-ui/icons/20/Check2'
import { Pause } from 'blaze-ui/icons/20/Pause'
import { Play3 } from 'blaze-ui/icons/20/Play3'
import { Skeleton } from 'components/Skeleton/Skeleton'
import { useAudioSample } from 'hooks/useAudioSample'
import { useBrandKitAvatars } from 'hooks/queries/brandKits/useBrandKitAvatars'
import { BrandKitAvatar } from 'types'
import { MissingAvatarsCallout } from 'components/MissingAvatarsCallout/MissingAvatarsCallout'
import Styles from './AvatarPickerModal.module.scss'

interface AvatarPickerModalProps {
  brandKitId: number
  selectedAvatarId?: number
  onSelect: (avatar: BrandKitAvatar) => void
}

export const isAvatarInRotation = (avatar: BrandKitAvatar) =>
  avatar.enabled && avatar.verification_status === 'verified'

export const AvatarPickerModal = ({ brandKitId, selectedAvatarId, onSelect }: AvatarPickerModalProps) => {
  const { closeModal } = useModals()
  const { data: avatars, isLoading } = useBrandKitAvatars({ brandKitId })
  const available = useMemo(() => (avatars ?? []).filter(isAvatarInRotation), [avatars])
  const { playingKey, toggle } = useAudioSample()

  const handleSelect = (avatar: BrandKitAvatar) => {
    onSelect(avatar)
    closeModal()
  }

  return (
    <Modal.Root size="lg" blaze onPressOutside={closeModal} aria-label="Choose an avatar">
      <Modal.Header>
        <Heading level={2}>Choose an avatar</Heading>
      </Modal.Header>
      <Modal.Content withoutFooter>
        {isLoading && <Skeleton active paragraph={{ rows: 3 }} title={false} />}
        {!isLoading && available.length === 0 && <MissingAvatarsCallout />}
        {!isLoading && available.length > 0 && (
          <div className={Styles.grid}>
            {available.map((avatar) => {
              const isSelected = avatar.id === selectedAvatarId
              const sampleUrl = avatar.voice?.sample_audio_url ?? null
              const isPlaying = !!sampleUrl && playingKey === String(avatar.id)
              return (
                <div key={avatar.id} className={cx(Styles.cardWrap, { [Styles.selected]: isSelected })}>
                  <div className={Styles.photoWrap}>
                    {avatar.photo_url && <img className={Styles.photo} src={avatar.photo_url} alt="" />}
                    {sampleUrl && (
                      <button
                        type="button"
                        className={Styles.playButton}
                        aria-label={isPlaying ? 'Pause voice sample' : 'Play voice sample'}
                        onClick={() => toggle(String(avatar.id), sampleUrl)}
                      >
                        {isPlaying ? <Pause /> : <Play3 fill="currentColor" />}
                      </button>
                    )}
                    {isSelected && (
                      <span className={Styles.checkBadge} role="img" aria-label="Selected">
                        <Check2 />
                      </span>
                    )}
                  </div>
                  <div className={Styles.cardBody}>
                    <Text>{avatar.name}</Text>
                    {avatar.persona_note && (
                      <Text variant="secondary" color="var(--dark-60)" lineClamp={1}>
                        {avatar.persona_note}
                      </Text>
                    )}
                  </div>
                  <button
                    type="button"
                    className={Styles.selectButton}
                    aria-label={`Select ${avatar.name}`}
                    onClick={() => handleSelect(avatar)}
                  />
                </div>
              )
            })}
          </div>
        )}
      </Modal.Content>
    </Modal.Root>
  )
}
